import { FC, useEffect, useMemo, useState } from 'react';

import { useLazyQuery, useMutation } from '@apollo/client';
import LoadingButton from '@mui/lab/LoadingButton';
import {
    Box,
    Button,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';
import { isEmpty } from '@npi/buddy-utils';
import { AdobeJobStatus, ApplicationStatus, ServiceStatus } from '@prisma/client';
import { Formik } from 'formik';

import { useParams } from '@redwoodjs/router';
import { useQuery } from '@redwoodjs/web';

import { Flex } from 'src/components/Flex';
import Loader from 'src/components/Loader/Loader';
import { MainCardWithHeader } from 'src/components/MainCardWithHeader';
import { ConfirmationModal } from 'src/components/Modals';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import { useAuthContext } from 'src/contexts/AuthProvider';
import { ProductOrderProvider } from 'src/contexts/ProductOrderProvider';
import { ServiceOrderProvider } from 'src/contexts/ServiceOrderProvider';
import {
    EMAIL_DOCUMENT_FOR_SIGNING,
    GENERATE_DOCUMENT_FOR_SIGNING,
} from 'src/graphql/mutations/documentForSigning.mutation';
import { RESEND_AGREEMENT } from 'src/graphql/mutations/resendAgreement.mutation';
import {
    ACTIVATE_SERVICE,
    REQUEST_SERIVCE_APPLICATION_BUILD,
    SUBMIT_SERIVCE_APPLICATION,
} from 'src/graphql/mutations/submitServiceApplication.mutation';
import { UPDATE_MERCHANT_APPLICATION_STATUS } from 'src/graphql/mutations/updateMerchantApplicationStatus.mutation';
import { GET_AUDIT_LOGS } from 'src/graphql/queries/auditLogs.query';
import { GET_AGREEMENT_STATUS } from 'src/graphql/queries/getAgreementStatus.query';
import { GET_LATEST_ADOBE_SIGN_JOB } from 'src/graphql/queries/merchantApplications.query';
import { GET_MERCHANT_DASHBOARD } from 'src/graphql/queries/merchants.query';
import AddServiceOutletInformation from 'src/pages/AddServicePage/steps/components/OutletInformation';
import ServiceOrderManagementDashboard from 'src/pages/AddServicePage/steps/ServiceOrderManagementDashboard';
import { getMinimumContractTerm, onError } from 'src/utils/utils';

import ServiceTotals from '../../components/ServiceTotals';
import SummaryOrderLists from '../../components/SummaryOrdersList';
import { ChargesValue } from '../ServiceRequirements';

import { CaptureSignatureSection } from './components/CaptureSignatureSection';
import InformationSection from './components/InformationSection';
import { PrincipleSection } from './components/PrincipleSection';
import { RisksSection } from './components/RisksSection';

interface SummaryInformationProps {
    handleNext?: () => void;
    isAddService?: boolean;
}

const SummaryInformation: FC<SummaryInformationProps> = ({ isAddService = false }) => {
    const { merchantId: paramMerchantId } = useParams();
    const {
        submitMerchantApplication,
        merchantApplication,
        serviceApplication,
        handleBack,
        refetchUpdateMerchantApplication,
        setMerchantApplication,
        setServiceApplication,
    } = useAddMerchantContext();
    const merchantId = paramMerchantId ?? merchantApplication?.id;
    const { usersCurrency } = useAuthContext();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [email, setEmail] = useState('');
    const [signingUrl, setSigningUrl] = useState('');
    const [actionType, setActionType] = useState('');
    const [emailSent, setEmailSent] = useState<boolean>(false);
    const [selectedPrincipalIndex, setSelectedPrincipalIndex] = useState<number | null>(null);

    const [signer, setSigner] = useState({
        firstName: '',
        lastName: '',
        email: '',
    });
    const [serviceApplicationStatus, setServiceApplicationStatus] = useState<ApplicationStatus>(
        serviceApplication?.status
    );
    const [serviceApplicationID, setServiceApplicationID] = useState<number | null>(
        serviceApplication?.id ?? merchantApplication?.id ?? null
    );
    const [isResendDisabled, setIsResendDisabled] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [serviceApplicationLogs, setServiceApplicationLogs] = useState([]);

    const { data: logData, refetch: refetchLogData } = useQuery(GET_AUDIT_LOGS, {
        fetchPolicy: 'cache-and-network',
        onError: () => {
            return showErrorToast('There was an error retrieving the audit logs');
        },
    });
    useEffect(() => {
        if (logData?.auditLogs) {
            const filteredLogs = logData.auditLogs.filter((log) => {
                return log.entityId === serviceApplicationID && log.entity === 'SERVICE';
            });
            setServiceApplicationLogs(filteredLogs);
        }
    }, [logData, serviceApplicationID]);

    const [generateOnlineDocumentation, { loading: isGenerating }] = useMutation(
        GENERATE_DOCUMENT_FOR_SIGNING,
        {
            onError: (error) => {
                showErrorToast(
                    error.message ?? 'We were unable to generate your on-screen agreement'
                );
            },
            onCompleted: ({ displayDocumentsForSigningOnScreen }) => {
                setSigningUrl(displayDocumentsForSigningOnScreen?.url);
                if (serviceApplication) {
                    submitServiceApplication({
                        variables: {
                            input: {
                                status: ServiceStatus.DRAFT,
                                merchantId,
                                applicationId: serviceApplicationID,
                            },
                        },
                    });
                } else {
                    submitMerchantApplication();
                }
            },
        }
    );

    const [emailDocumentForSigning, { loading: isSending }] = useMutation(
        EMAIL_DOCUMENT_FOR_SIGNING,
        {
            onError: (error) => {
                showErrorToast(error.message ?? 'We were unable to send your agreement');
            },
            onCompleted: () => {
                showSuccessToast('Check your inbox to complete the agreement signature process');

                if (serviceApplication) {
                    submitServiceApplication({
                        variables: {
                            input: {
                                status: ServiceStatus.DRAFT,
                                merchantId,
                                applicationId: serviceApplicationID,
                            },
                        },
                    });
                } else {
                    handleRefresh();
                    submitMerchantApplication();
                }
                setIsOpen(false);
                setEmailSent(true);
            },
        }
    );

    const onSendEmail = () => {
        setIsOpen(true);
        setActionType('sendEmail');
    };

    const onCaptureOnScreen = () => {
        setIsOpen(true);
        setActionType('generateAgreement');
    };

    const handleClose = () => {
        setIsOpen(false);
        setEmail('');
        setActionType('');
        setSelectedPrincipalIndex(null);
    };

    const handleSubmit = async () => {
        if (actionType === 'sendEmail' && !isSending && !emailSent) {
            await emailDocumentForSigning({
                variables: {
                    input: {
                        applicationId: serviceApplication?.id ?? merchantApplication.id,
                        membersInfo: [{ email }],
                        selectedPrincipalIndex,
                    },
                },
            });
        } else if (actionType === 'generateAgreement' && !isGenerating) {
            await generateOnlineDocumentation({
                variables: {
                    input: {
                        applicationId: serviceApplication?.id ?? merchantApplication.id,
                        membersInfo: [{ email }],
                        selectedPrincipalIndex,
                    },
                },
            });
        }
    };

    const beneficialOwnerEmailAddresses = useMemo(() => {
        return (merchantApplication?.merchantInformation?.principals ?? []).map((person, index) => {
            return {
                email: person.email,
                name: `${person.firstName} ${person.lastName}`,
                index,
            };
        });
    }, [merchantApplication]);

    const formikInitialValues = {
        salesConditions: {
            merchantCategoryCode: serviceApplication
                ? serviceApplication?.serviceRequirements?.salesConditions?.merchantCategoryCode
                : null,
            monthlyTurnover: serviceApplication
                ? serviceApplication?.serviceRequirements?.salesConditions?.monthlyTurnover
                : '',
        },
        service: {
            serviceType: '',
            contractTerm: '',
            selectedTerminalOffer: '',
            selectedOnlineOffer: '',
            selectedTransactionOffer: '',
            transactionPricingOfferId: '',
        },
        order: {
            primary: [],
            secondary: [],
        },
        totals: { totalOneOff: null, totalMonthly: null, totalTerm: null },
        totalOrderPrice: {
            totalMonthly: [],
            totalOneOff: [],
        },
        transactionCharges: {
            personalCards: {} as ChargesValue,
            commercialCards: {} as ChargesValue,
            additionalTariffs: {} as ChargesValue,
            pciComplience: {} as ChargesValue,
        },
        isStepCompleted: false,
        ...(isAddService
            ? serviceApplication?.serviceRequirements
            : merchantApplication?.serviceRequirements),
    };

    const onRefreshAll = async () => {
        await refetchAgreementStatus();
        await refetchLogData();

        const { data } = await refetchApplication();

        if (data?.retrieveMerchantApplication) {
            setMerchantApplication(data.retrieveMerchantApplication);
        } else if (data?.retrieveServiceApplication) {
            setServiceApplication(data.retrieveServiceApplication);
        }

        const updatedApplication = await refetchUpdateMerchantApplication({
            variables: { id: serviceApplicationID },
        });

        setServiceApplication(updatedApplication?.data?.retrieveMerchantApplication);
        setServiceApplicationStatus(updatedApplication?.data?.retrieveMerchantApplication.status);
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await onRefreshAll();
            showSuccessToast('Agreement status has been refreshed.');
        } catch (error) {
            showErrorToast(error.message);
        } finally {
            setIsRefreshing(false);
        }
    };

    const [updateMerchantApplicationStatus, { loading: updatingMerchantApplication }] = useMutation(
        UPDATE_MERCHANT_APPLICATION_STATUS,
        {
            onCompleted: async () => {
                await handleRefresh();
            },
        }
    );

    const onApplicationStatusUpdate = async (status: ApplicationStatus) => {
        if (isAddService) {
            setServiceApplicationStatus(status);
            showSuccessToast('Application Status Updated!');
            await updateMerchantApplicationStatus({
                variables: {
                    status,
                    applicationId: serviceApplicationID,
                    merchantId: merchant?.id,
                },
            });
        }
    };

    const { refetch: refetchMerchant, data: merchantInformation } = useQuery(
        GET_MERCHANT_DASHBOARD,
        {
            skip: !isAddService,
            fetchPolicy: 'cache-and-network',
            variables: {
                id: merchantId,
            },
            onError: () => {
                if (isAddService) {
                    showErrorToast(`Error fetching the Merchant id ${merchantId}`);
                }
            },
        }
    );
    const merchant = merchantInformation?.merchantDashboard;
    const [submitServiceApplication] = useMutation(SUBMIT_SERIVCE_APPLICATION, {
        onError: (error) => {
            showErrorToast(error.message);
        },
    });
    const [requestServiceApplicationBuild, { loading: processServiceRequestBuild }] = useMutation(
        REQUEST_SERIVCE_APPLICATION_BUILD,
        {
            onError: (error) => {
                showErrorToast(error.message);
            },
            onCompleted: () => {
                refetchMerchant({
                    id: merchantId,
                });
                showSuccessToast('Request Build Processed.');
                showSuccessToast('Email sent.');
                handleRefresh();
            },
        }
    );

    const handleProcessServiceBuild = ({ terminalId }) => {
        if (isAddService) {
            requestServiceApplicationBuild({
                variables: {
                    input: {
                        status: ServiceStatus.DRAFT,
                        merchantId,
                        terminalId,
                        applicationId: serviceApplicationID,
                    },
                },
            });
        }
    };

    const [activateService] = useMutation(ACTIVATE_SERVICE, {
        onCompleted: async () => {
            await onApplicationStatusUpdate(ApplicationStatus.ACTIVE);
            showSuccessToast('Service Order Completed!');
        },
    });

    const handleCompleteOrder = async () => {
        if (isAddService) {
            await activateService({
                variables: {
                    input: {
                        status: ServiceStatus.ACTIVE,
                        applicationId: serviceApplicationID,
                    },
                },
            });
        }
    };

    const [getAgreementStatus, { data: agreementStatusData, refetch: refetchAgreementStatus }] =
        useLazyQuery(GET_AGREEMENT_STATUS, {
            onError: (err) => {
                return showErrorToast(err.message);
            },
        });

    const isAgreementSigned = useMemo(() => {
        return agreementStatusData?.agreementStatus?.status === AdobeJobStatus.SIGNED;
    }, [agreementStatusData]);

    const previewUrl = useMemo(() => {
        return agreementStatusData?.agreementStatus?.previewUrl ?? '';
    }, [agreementStatusData]);

    const { data: latestJobData } = useQuery(GET_LATEST_ADOBE_SIGN_JOB, {
        variables: { applicationId: serviceApplicationID },
    });

    const [resendAgreement, { loading: resendingLoading }] = useMutation(RESEND_AGREEMENT, {
        onError,
        onCompleted: () => {
            showSuccessToast(
                'Agreement has been resent. Please wait 24 hours before sending another reminder.'
            );
        },
    });

    const onResendAgreement = async () => {
        await resendAgreement({
            variables: { applicationId: serviceApplicationID },
        });
        setIsResendDisabled(true);
    };

    const {
        loading: fetchingApplication,
        data,
        refetch: refetchApplication,
    } = useQuery(GET_LATEST_ADOBE_SIGN_JOB, {
        fetchPolicy: 'cache-and-network',
        variables: { applicationId: serviceApplicationID },
    });

    useEffect(() => {
        if (data?.retrieveMerchantApplication?.id) {
            getAgreementStatus({
                variables: { applicationId: data?.retrieveMerchantApplication?.id },
            });
        }
    }, [data?.retrieveMerchantApplication?.id, getAgreementStatus]);

    useEffect(() => {
        if (latestJobData?.latestAdobeSignJob) {
            setIsResendDisabled(!latestJobData.latestAdobeSignJob.canResend);
        }
    }, [latestJobData]);

    useEffect(() => {
        if (serviceApplicationID) {
            getAgreementStatus({
                variables: { applicationId: serviceApplicationID },
            });
        }
    }, [serviceApplicationID]);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (isAddService) {
            refetchMerchant();
            setSigner({
                firstName: merchant?.firstName,
                lastName: merchant?.lastName,
                email: merchant?.email,
            });

            setServiceApplicationStatus(serviceApplication?.status);

            if (serviceApplication?.id) {
                setServiceApplicationID(serviceApplication?.id);
            }
        }
    }, [merchant, refetchMerchant, serviceApplication, agreementStatusData, serviceApplicationID]);

    const onBeneficialOwnerEmailAddressChange = (e) => {
        const index = parseInt(e.target.value, 10);
        setSelectedPrincipalIndex(index);
        setEmail(beneficialOwnerEmailAddresses[index].email);
    };

    const loadingModalButtonText = isAddService ? 'Service' : 'Merchant';
    return (
        <>
            {fetchingApplication || updatingMerchantApplication || isRefreshing ? <Loader /> : null}
            {serviceApplicationStatus !== ApplicationStatus.DRAFT && isAddService ? (
                <>
                    <ServiceOrderManagementDashboard
                        serviceApplication={serviceApplication}
                        merchant={{
                            merchantName: merchant?.tradingName,
                            mid: merchant?.mid,
                            signer,
                        }}
                        auditLog={serviceApplicationLogs}
                        serviceApplicationStatus={serviceApplicationStatus}
                        serviceApplicationID={serviceApplicationID}
                        isAgreementSigned={isAgreementSigned}
                        onApplicationStatusUpdate={onApplicationStatusUpdate}
                        handleCompleteOrder={handleCompleteOrder}
                        processingRequestBuild={processServiceRequestBuild}
                        handleProcessServiceBuild={handleProcessServiceBuild}
                        onResendAgreement={onResendAgreement}
                        resendingLoading={resendingLoading}
                        isResendDisabled={isResendDisabled}
                        handleRefresh={handleRefresh}
                        isRefreshing={
                            isRefreshing || fetchingApplication || updatingMerchantApplication
                        }
                        agreementPreviewUrl={previewUrl}
                    />
                </>
            ) : (
                <form>
                    <Box display="flex" flexDirection="column" gap="12px">
                        <CaptureSignatureSection
                            onSendEmail={onSendEmail}
                            onCaptureOnScreen={onCaptureOnScreen}
                            signingUrl={signingUrl}
                            merchantApplicationStatus={
                                serviceApplication?.status ?? merchantApplication?.status
                            }
                            showModal={!isEmpty(signingUrl)}
                            clearSigningUrl={() => {
                                setSigningUrl('');
                                handleRefresh();
                            }}
                            isCapturingOnScreen={isGenerating}
                            isCreatingAgreement={isSending}
                        />
                        {!isAddService && (
                            <>
                                {' '}
                                <PrincipleSection />
                            </>
                        )}
                        <RisksSection isAddService={isAddService} />
                        {!isAddService ? (
                            <InformationSection />
                        ) : (
                            <>
                                {serviceApplication && (
                                    <Formik
                                        initialValues={formikInitialValues}
                                        onSubmit={() => {}}
                                        enableReinitialize
                                    >
                                        {(formik) => {
                                            useEffect(() => {
                                                if (serviceApplication || merchantApplication) {
                                                    formik.resetForm({
                                                        values: formikInitialValues,
                                                    });
                                                }
                                            }, [serviceApplication]);
                                            const contractTermInMonths = getMinimumContractTerm(
                                                serviceApplication?.serviceRequirements
                                            );

                                            return (
                                                <Flex gap={3} flexDirection={'column'}>
                                                    <ServiceTotals
                                                        chargeableContractTerm={
                                                            serviceApplication?.serviceRequirements
                                                                ?.productOrder?.term
                                                        }
                                                        minimumContractTerm={contractTermInMonths}
                                                    />

                                                    <AddServiceOutletInformation />
                                                    <MainCardWithHeader
                                                        heading={'Service Order Details'}
                                                    >
                                                        <ProductOrderProvider>
                                                            <ServiceOrderProvider>
                                                                <SummaryOrderLists
                                                                    orders={
                                                                        serviceApplication
                                                                            ?.serviceRequirements
                                                                            ?.productOrder
                                                                    }
                                                                    currency={usersCurrency}
                                                                    contractTerm={
                                                                        serviceApplication
                                                                            ?.serviceRequirements
                                                                            ?.productOrder?.term
                                                                    }
                                                                />
                                                            </ServiceOrderProvider>
                                                        </ProductOrderProvider>
                                                    </MainCardWithHeader>
                                                </Flex>
                                            );
                                        }}
                                    </Formik>
                                )}

                                <Box display="flex" justifyContent="flex-end">
                                    <Button
                                        sx={{
                                            alignSelf: 'end',
                                            marginTop: '24px',
                                        }}
                                        type="button"
                                        variant="main"
                                        onClick={handleBack}
                                    >
                                        Back
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Box>
                    <ConfirmationModal
                        modalWidth="md"
                        hideConfirmButton
                        hideCancelButton
                        title={
                            <Typography variant="h2" component="h2" align="center">
                                {actionType === 'sendEmail'
                                    ? 'Select a principal to receive the agreement via email:'
                                    : 'Select a principal to generate the on-screen agreement:'}
                            </Typography>
                        }
                        message={
                            <>
                                <Typography
                                    variant="subtitle1"
                                    gutterBottom
                                    sx={{
                                        textAlign: 'center',
                                    }}
                                >
                                    Successful completion will create the
                                    {isAddService ? ' service' : ' merchant'}...
                                </Typography>
                                <Flex gap={1} flexDirection="column" alignItems={'center'}>
                                    <FormControl>
                                        <RadioGroup
                                            name="beneficialOwnerEmailAddresses"
                                            onChange={(e) => {
                                                return onBeneficialOwnerEmailAddressChange(e);
                                            }}
                                            value={
                                                selectedPrincipalIndex !== null
                                                    ? selectedPrincipalIndex.toString()
                                                    : ''
                                            }
                                        >
                                            {beneficialOwnerEmailAddresses.map((person, index) => {
                                                return (
                                                    <FormControlLabel
                                                        key={`${person.email}-${index}`}
                                                        value={index.toString()}
                                                        control={<Radio />}
                                                        label={`${person.name} - (${person.email})`}
                                                        disabled={
                                                            (emailSent &&
                                                                actionType === 'sendEmail') ||
                                                            (actionType === 'generateAgreement' &&
                                                                selectedPrincipalIndex !== index &&
                                                                emailSent)
                                                        }
                                                    />
                                                );
                                            })}
                                        </RadioGroup>
                                    </FormControl>
                                    <LoadingButton
                                        onClick={handleSubmit}
                                        disabled={
                                            selectedPrincipalIndex === null ||
                                            (actionType === 'sendEmail' && emailSent) ||
                                            isGenerating ||
                                            isSending
                                        }
                                        loading={isGenerating || isSending}
                                        loadingPosition="start"
                                        variant="npi-primary"
                                        color="primary"
                                        sx={{
                                            mt: 2,
                                            bgcolor: 'primary.main',
                                            '.MuiLoadingButton-loadingIndicator': {
                                                color: 'primary.contrastText',
                                            },
                                        }}
                                    >
                                        {actionType === 'sendEmail'
                                            ? `Send Email & Create ${loadingModalButtonText}`
                                            : `Generate Agreement & Create ${loadingModalButtonText}`}
                                    </LoadingButton>
                                </Flex>
                            </>
                        }
                        isOpen={isOpen}
                        onCancel={() => {
                            return handleClose();
                        }}
                    />
                </form>
            )}
        </>
    );
};
export default SummaryInformation;
